import axios from "axios";
import {
  Job,
  Instance,
  Billing,
  JobArtifacts,
  JobLog,
  HealthCheck,
  JobSubmission,
  JobSource,
  JobNotification,
  Feature,
  InstanceXFeature,
  JobXFeature,
  MagicLinkRequest,
  MagicLinkResponse,
  VerifyMagicLinkResponse,
  FeatureUpdate,
  InstanceFeatureUpdate,
  JobFeatureUpdate,
  PriceSetting,
  Tier,
  TierFeatures,
  TierFeatureUpdate,
  PriceUpdate,
  ConverterDemoResponse,
  ConverterResponse,
  CreateShareURLResponse,
  GetShareURLResponse,
  AddReferralRequest,
  AddReferralResponse,
  DemoConversionAdminResponse,
  FeatureStatus,
  ConverterJob,
  ConverterJobSubmissionCode,
  ConverterJobSubmissionFile,
  ConverterJobResult,
  WebUsageDay,
  WebUsageDetailResponse,
  WebUsageListResponse,
  ApiUsageListResponse,
  ApiUsageDetailResponse,
  ApiUsageDay,
} from "../types";
import { DashboardData } from "../features/dashboard/types";
import { adminApi } from "./adminApi";

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:8090";

// The API clamps any `limit` to this value.
export const MAX_PAGE_LIMIT = 1000;

// One page of job rows. `total` is the full count, so the caller can tell
// when the page hides rows.
export interface JobRows<T> {
  items: T[];
  total: number;
}

// Storage key for API credentials
const STORAGE_KEY = "alchemist_api_credentials";

// Stored credentials interface with backwards-compatible instanceMode
interface StoredCredentials {
  apiKey: string;
  instanceKey: string;
  name: string;
  instanceMode?: 'temp' | 'persistent'; // Optional for backwards compat
  email?: string;
}

interface SaveInstanceResponse {
  name: string;
  pending_email: string;
}

// Get stored credentials with backwards-compatible migration
function getStoredCredentials(): StoredCredentials | null {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored) {
    try {
      const parsed = JSON.parse(stored);
      if (!parsed?.apiKey) {
        return null;
      }
      return {
        ...parsed,
        instanceMode: parsed.instanceMode ?? 'temp' // Default for legacy
      };
    } catch {
      return null;
    }
  }
  return null;
}

// Store credentials
function storeCredentials(credentials: StoredCredentials) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(credentials));
}

// Clear credentials
function clearCredentials() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.error('Failed to remove credentials from localStorage:', error);
  }
}

// Create axios instance with dynamic headers
const api = axios.create({
  baseURL: API_BASE_URL,
});

declare module "axios" {
  interface InternalAxiosRequestConfig {
    // The stored instance key the request interceptor injected.
    storedKey?: string;
  }
}

// Add request interceptor to include API key if available
api.interceptors.request.use(
  (config) => {
    const credentials = getStoredCredentials();
    if (credentials && credentials.apiKey && !config.headers["X-API-Key"]) {
      config.headers["X-API-Key"] = credentials.apiKey;
      config.storedKey = credentials.apiKey;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// Routes where a 401 means the stored instance key is invalid. The auth and
// admin routes return 401 for other reasons, such as a bad magic link.
function usesInstanceAuth(url: string): boolean {
  return url.startsWith("/api/v1/") && !url.startsWith("/api/v1/auth/") && !url.startsWith("/api/v1/admin/");
}

// Retry only when the current rate-limit window is about to reset.
const MAX_RATE_LIMIT_RETRIES = 2;
const MAX_RATE_LIMIT_WAIT_MS = 3000;
const RATE_LIMIT_JITTER_MS = 250;
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config;
    // A revoked or bogus stored key: sign out, so the app does not stay on a dead session.
    // The key check skips stale responses and repeated redirects.
    if (
      error.response?.status === 401 &&
      config?.storedKey &&
      config.storedKey === getStoredCredentials()?.apiKey &&
      usesInstanceAuth(config.url ?? "")
    ) {
      clearCredentials();
      window.location.assign("/");
      return Promise.reject(error);
    }
    if (error.response?.status === 429 && config && (config.rateLimitRetries ?? 0) < MAX_RATE_LIMIT_RETRIES) {
      const resetSeconds = Number(error.response.headers?.["x-ratelimit-reset"]);
      if (!Number.isInteger(resetSeconds) || resetSeconds <= 0) {
        return Promise.reject(error);
      }

      // Wait through the following second because the server truncates sub-second reset times.
      const waitMs = Math.max(0, (resetSeconds + 1) * 1000 - Date.now());
      if (waitMs > MAX_RATE_LIMIT_WAIT_MS) {
        return Promise.reject(error);
      }

      config.rateLimitRetries = (config.rateLimitRetries ?? 0) + 1;
      const delayMs = waitMs + Math.random() * RATE_LIMIT_JITTER_MS;
      await new Promise((resolve) => setTimeout(resolve, delayMs));
      return api(config);
    }
    return Promise.reject(error);
  },
);

export const apiService = {
  // Registration
  async register(name: string, contactEmail?: string, refSource?: string): Promise<{ apiKey: string; instanceKey: string; name: string }> {
    const response = await api.post<{ api_key: string; instance_key: string; name: string }>("/register", {
      name,
      contact_email: contactEmail,
      ref_source: refSource,
    });

    if (!response.data || !response.data.api_key) {
      throw new Error('Invalid response from server');
    }

    const credentials = {
      apiKey: response.data.api_key || '',
      instanceKey: response.data.instance_key || '',
      name: response.data.name || '',
    };
    
    // Check if any values are empty
    if (!credentials.apiKey || !credentials.instanceKey || !credentials.name) {
      throw new Error('Invalid credentials received from server');
    }

    // Store credentials
    storeCredentials(credentials);

    return credentials;
  },

  // Check if registered
  isRegistered(): boolean {
    return getStoredCredentials() !== null;
  },

  // Get current instance info
  getInstanceInfo(): { instanceKey: string; name: string; instanceMode: 'temp' | 'persistent'; email?: string } | null {
    const credentials = getStoredCredentials();
    if (credentials) {
      return {
        instanceKey: credentials.instanceKey,
        name: credentials.name,
        instanceMode: credentials.instanceMode ?? 'temp',
        email: credentials.email,
      };
    }
    return null;
  },

  // Clear registration
  clearRegistration() {
    clearCredentials();
  },

  // Add referral source
  async addReferral(refSource: string): Promise<AddReferralResponse> {
    const request: AddReferralRequest = {
      ref_source: refSource,
    };

    const response = await api.post<AddReferralResponse>("/api/v1/instances/referrals", request);
    return response.data;
  },

  // Health check (default without metrics)
  async getHealth(): Promise<HealthCheck> {
    const response = await api.get<HealthCheck>("/health");
    return response.data;
  },

  // Job operations
  async submitJob(file: File, data: JobSubmission): Promise<{ job_id: string; status: string; position_in_queue?: number }> {
    const formData = new FormData();
    formData.append("sources", file);
    formData.append("data", JSON.stringify(data));

    const response = await api.post<{ job_id: string; status: string; position_in_queue?: number }>("/api/v1/jobs", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  },

  async getJobs(
    page: number = 1,
    status?: string,
    options?: { limit?: number; search?: string; sort?: string; order?: 'asc' | 'desc' }
  ): Promise<{ jobs: Job[]; total: number; page: number; total_pages: number; status_counts?: Record<string, number> }> {
    const params: any = { page, limit: options?.limit ?? 20 };
    if (status) params.status = status;
    if (options?.search) params.search = options.search;
    if (options?.sort) params.sort = options.sort;
    if (options?.order) params.order = options.order;

    const response = await api.get<{ jobs: Job[]; total: number; page: number; total_pages: number; status_counts?: Record<string, number> }>(
      "/api/v1/jobs",
      { params }
    );
    return response.data;
  },

  async getJob(jobId: string): Promise<Job> {
    const response = await api.get<Job>(`/api/v1/jobs/${jobId}`);
    return response.data;
  },

  async deleteJob(jobId: string): Promise<void> {
    await api.delete(`/api/v1/jobs/${jobId}`);
  },


  async getJobLogs(jobId: string, afterId?: number): Promise<JobLog[]> {
    const params: any = { limit: MAX_PAGE_LIMIT };
    if (afterId) params.after_id = afterId;

    const response = await api.get<JobLog[]>(`/api/v1/jobs/${jobId}/logs`, { params });
    return response.data;
  },

  // Follows the after_id cursor until the last batch, so long logs are not cut at the page cap.
  async getAllJobLogs(jobId: string): Promise<JobLog[]> {
    const all: JobLog[] = [];
    let afterId: number | undefined;
    for (;;) {
      const batch = await this.getJobLogs(jobId, afterId);
      all.push(...batch);
      if (batch.length < MAX_PAGE_LIMIT) break;
      afterId = batch[batch.length - 1].id;
    }
    return all;
  },

  // Artifacts
  async getJobArtifacts(jobId: string): Promise<JobArtifacts> {
    const response = await api.get<JobArtifacts>(`/api/v1/jobs/${jobId}/artifacts`);
    return response.data;
  },

  async downloadArtifact(jobId: string, artifactPath: string): Promise<Blob> {
    const response = await api.get(`/api/v1/jobs/${jobId}/artifacts/${artifactPath}/download`, {
      responseType: "blob",
      validateStatus: () => true, // Don't throw on any status
    });
    
    // Handle non-2xx responses
    if (response.status < 200 || response.status >= 300) {
      let error: any;
      try {
        const text = await response.data.text();
        const errorData = JSON.parse(text);
        error = new Error(errorData.message || "");
        error.response = { data: errorData, status: response.status };
      } catch {
        error = new Error("Could not download file");
        error.response = { status: response.status };
      }
      throw error;
    }
    
    return response.data;
  },

  // Dashboard Data
  async getDashboardData(jobId: string): Promise<DashboardData> {
    const response = await api.get<DashboardData>(`/api/v1/jobs/${jobId}/dashboard`);
    return response.data;
  },

  // Job Sources
  // The job detail view filters, sorts, and counts on the client, so it asks
  // for the server cap in one page. `total` reveals when the job has more.
  async getJobSources(jobId: string): Promise<JobRows<JobSource>> {
    const response = await api.get<{ sources: JobSource[]; total: number }>(
      `/api/v1/jobs/${jobId}/sources`,
      { params: { limit: MAX_PAGE_LIMIT, offset: 0 } }
    );
    return { items: response.data.sources ?? [], total: response.data.total ?? 0 };
  },

  // Job Notifications
  async getJobNotifications(jobId: string): Promise<JobRows<JobNotification>> {
    const response = await api.get<{ notifications: JobNotification[]; total: number }>(
      `/api/v1/jobs/${jobId}/notifications`,
      { params: { limit: MAX_PAGE_LIMIT, offset: 0 } }
    );
    return { items: response.data.notifications ?? [], total: response.data.total ?? 0 };
  },

  // Contact Request (for converter, analyzer+, help)
  async submitContactRequest(data: { request_type: string; email: string; note: string }): Promise<void> {
    await api.post("/api/v1/contact-request", data);
  },

  // Instance operations
  async getInstance(): Promise<Instance> {
    const credentials = getStoredCredentials();
    if (!credentials?.instanceKey) {
      throw new Error('No instance key found');
    }
    const response = await api.get<Instance>(`/api/v1/instances/${credentials.instanceKey}`);
    return response.data;
  },

  async getBilling(): Promise<Billing> {
    const response = await api.get<Billing>('/api/v1/billing');
    return response.data;
  },

  async deleteInstance(): Promise<void> {
    const credentials = getStoredCredentials();
    if (!credentials || !credentials.instanceKey) {
      throw new Error('No instance key found');
    }
    await api.delete(`/api/v1/instances/${credentials.instanceKey}`);
  },

  // Request an email confirmation to save an instance or change its email.
  async saveInstance(email: string, name?: string): Promise<SaveInstanceResponse> {
    const response = await api.post<SaveInstanceResponse>("/api/v1/instance/save", {
      email,
      name,
    });

    // A requested name is safe to apply before email confirmation.
    const credentials = getStoredCredentials();
    if (credentials && credentials.name !== response.data.name) {
      storeCredentials({
        ...credentials,
        name: response.data.name,
      });
    }

    return response.data;
  },

  // Request a sign-in link by email (public endpoint, anti-enumeration response)
  async requestLoginLink(email: string): Promise<{ message: string }> {
    const response = await api.post<{ message: string }>("/api/v1/auth/login", { email });
    return response.data;
  },

  // Sign up by email (public endpoint, anti-enumeration response): creates a
  // persistent instance for a new email and sends a 30-minute access link.
  // The instance gets a default server-generated name; rename it in Settings.
  async signUp(email: string): Promise<{ message: string }> {
    const response = await api.post<{ message: string }>("/api/v1/auth/signup", { email });
    return response.data;
  },

  // Rename the current instance (instance_key stays unchanged)
  async renameInstance(name: string): Promise<{ name: string }> {
    const response = await api.post<{ name: string }>("/api/v1/instance/rename", { name });

    const credentials = getStoredCredentials();
    if (credentials) {
      storeCredentials({ ...credentials, name: response.data.name });
    }

    return response.data;
  },

  // Check a user magic link without consuming it
  async peekMagicLink(token: string): Promise<{ status: 'valid' | 'used' | 'expired' | 'invalid'; email?: string; instance_key?: string; instance_name?: string; purpose?: 'login' | 'save' }> {
    const response = await api.get<{ status: 'valid' | 'used' | 'expired' | 'invalid'; email?: string; instance_key?: string; instance_name?: string; purpose?: 'login' | 'save' }>(`/api/v1/auth/magic-link?token=${encodeURIComponent(token)}`);
    return response.data;
  },

  // Consume a user magic link
  async consumeMagicLink(token: string): Promise<{ api_key: string; instance_key: string; name: string; instance_mode: string; email: string }> {
    const response = await api.post<{ api_key: string; instance_key: string; name: string; instance_mode: string; email: string }>("/api/v1/auth/magic-link/consume", { token });
    return response.data;
  },

  // Store credentials from magic link verification
  storeCredentialsFromMagicLink(data: { api_key: string; instance_key: string; name: string; instance_mode: string; email: string }) {
    storeCredentials({
      apiKey: data.api_key,
      instanceKey: data.instance_key,
      name: data.name,
      instanceMode: data.instance_mode as 'temp' | 'persistent',
      email: data.email,
    });
  },

  // Sign out this instance in other browsers
  async signOutOtherSessions(): Promise<{ signed_out_count: number }> {
    const response = await api.post<{ signed_out_count: number }>("/api/v1/instance/sign-out-other-sessions");
    return response.data;
  },

  // Admin Authentication
  async sendMagicLink(request: MagicLinkRequest): Promise<MagicLinkResponse> {
    const response = await api.post<MagicLinkResponse>("/api/v1/admin/auth/magic-link", request);
    return response.data;
  },

  async verifyMagicLink(token: string): Promise<VerifyMagicLinkResponse> {
    const response = await api.get<VerifyMagicLinkResponse>(`/api/v1/admin/auth/verify?token=${token}`);
    return response.data;
  },

  async adminLogout(): Promise<void> {
    if (localStorage.getItem('admin_token')) {
      await adminApi.post("/api/v1/admin/auth/logout", {});
    }
  },

  // Admin Feature Management
  async getFeatures(): Promise<Feature[]> {
    const response = await adminApi.get<Feature[]>("/api/v1/admin/features");
    return response.data;
  },

  async updateFeatures(updates: FeatureUpdate[]): Promise<void> {
    await adminApi.put("/api/v1/admin/features", updates);
  },

  // Admin Pricing
  async getTierFeatures(): Promise<TierFeatures[]> {
    const response = await adminApi.get<TierFeatures[]>('/api/v1/admin/tiers/features');
    return response.data;
  },

  async updateTierFeatures(updates: TierFeatureUpdate[]): Promise<{ message: string; updated_instances: number }> {
    const response = await adminApi.put('/api/v1/admin/tiers/features', updates);
    return response.data;
  },

  async updateInstanceTier(instanceId: number, tier: Tier): Promise<void> {
    await adminApi.put('/api/v1/admin/instances/tier', { instance_id: instanceId, tier });
  },

  async updateInstanceCredits(instanceId: number, action: 'add' | 'set', tokens: number): Promise<{ message: string; credit_tokens: number }> {
    const response = await adminApi.put('/api/v1/admin/instances/credits', { instance_id: instanceId, action, tokens });
    return response.data;
  },

  async getPricing(): Promise<PriceSetting[]> {
    const response = await adminApi.get<PriceSetting[]>("/api/v1/admin/pricing");
    return response.data;
  },

  async updatePricing(update: PriceUpdate): Promise<void> {
    await adminApi.put("/api/v1/admin/pricing", update);
  },

  // Admin Instance Features (paged)
  async getInstanceFeatures(limit: number, offset: number, search?: string, sort?: string, order?: 'asc' | 'desc', ref?: string): Promise<{ items: InstanceXFeature[]; total: number }> {
    const params: any = { limit, offset };
    if (search) params.search = search;
    if (sort) params.sort = sort;
    if (order) params.order = order;
    if (ref) params.ref = ref;
    const response = await adminApi.get<{ items: InstanceXFeature[]; total: number }>("/api/v1/admin/instances/features", { params });
    return response.data;
  },

  async updateInstanceFeatures(updates: InstanceFeatureUpdate[]): Promise<void> {
    await adminApi.put("/api/v1/admin/instances/features", updates);
  },

  // Admin Job Features (paged)
  async getJobFeatures(limit: number, offset: number, search?: string, sort?: string, order?: 'asc' | 'desc'): Promise<{ items: JobXFeature[]; total: number }> {
    const params: any = { limit, offset };
    if (search) params.search = search;
    if (sort) params.sort = sort;
    if (order) params.order = order;
    const response = await adminApi.get<{ items: JobXFeature[]; total: number }>("/api/v1/admin/jobs/features", { params });
    return response.data;
  },

  async updateJobFeatures(updates: JobFeatureUpdate[]): Promise<void> {
    await adminApi.put("/api/v1/admin/jobs/features", updates);
  },

  async getJobAnalyzerLog(jobId: string): Promise<string> {
    const response = await adminApi.get<string>(`/api/v1/admin/jobs/${jobId}/analyzer-log`, { responseType: 'text' });
    return response.data;
  },

  async getConverterDemoAdmin(limit: number, offset: number, search?: string, sort?: string, order?: 'asc' | 'desc'): Promise<DemoConversionAdminResponse> {
    const params: any = { limit, offset };
    if (search) params.search = search;
    if (sort) params.sort = sort;
    if (order) params.order = order;
    const response = await adminApi.get<DemoConversionAdminResponse>("/api/v1/admin/converter-demo", { params });
    return response.data;
  },

  // Converter Demo endpoints
  async getConverterDemo(): Promise<ConverterDemoResponse> {
    const response = await api.get<ConverterDemoResponse>("/api/v1/converter-demo");
    return response.data;
  },

  async convertCode(sourceCode: string, aiEnabled?: boolean): Promise<ConverterResponse> {
    const response = await api.post<ConverterResponse>("/api/v1/converter-demo/convert", {
      source_code: sourceCode,
      ai_enabled: aiEnabled ?? true
    });
    return response.data;
  },

  async sendConverterFeedback(conversionId: string, isLiked: boolean): Promise<void> {
    await api.put("/api/v1/converter-demo/feedback", {
      conversion_id: conversionId,
      is_liked: isLiked
    });
  },

  // Dashboard Sharing
  async createShareURL(jobId: string): Promise<CreateShareURLResponse> {
    const response = await api.post<CreateShareURLResponse>(`/api/v1/jobs/${jobId}/dashboard/share`);
    return response.data;
  },

  async getShareURL(jobId: string): Promise<GetShareURLResponse | null> {
    const response = await api.get<GetShareURLResponse>(`/api/v1/jobs/${jobId}/dashboard/share`);
    return response.status === 204 ? null : response.data;
  },

  async revokeShareURL(jobId: string): Promise<void> {
    await api.delete(`/api/v1/jobs/${jobId}/dashboard/share`);
  },

  async rotateSharePassword(jobId: string): Promise<{ password: string }> {
    const response = await api.post(`/api/v1/jobs/${jobId}/dashboard/share/rotate-password`);
    return response.data;
  },

  // Single endpoint: validates password and returns dashboard data
  async getSharedDashboardData(token: string, password: string): Promise<DashboardData> {
    const response = await api.post<DashboardData>(`/share/dashboard/${token}`, {
      password: password
    });
    return response.data;
  },

  // Unified API keys (hosted API + CLI)
  async createApiKey(name: string, scopes: string[]) {
    return api.post('/api/v1/api-keys', { name, scopes });
  },

  async listApiKeys(params?: {
    limit?: number;
    offset?: number;
    sort?: string;
    order?: 'asc' | 'desc';
    search?: string;
    ids?: string;
    // CSV of scopes; a key matches when its effective scopes contain any of them.
    scope?: string;
  }) {
    return api.get('/api/v1/api-keys', { params });
  },

  async revokeApiKey(id: number) {
    return api.delete(`/api/v1/api-keys/${id}`);
  },

  // Converter (Alchemist API) endpoints
  async converterGetUsage(params?: { limit?: number; offset?: number; sort?: string; order?: 'asc' | 'desc'; api_key_ids?: string; status?: string; from?: string; to?: string }) {
    return api.get<ApiUsageListResponse>('/api/v1/api/usage', { params });
  },

  // One API request plus its usage entries. 404 if unknown.
  async converterGetUsageRequest(requestId: string, params?: { limit?: number; offset?: number; sort?: string; order?: 'asc' | 'desc' }) {
    return api.get<ApiUsageDetailResponse>('/api/v1/api/usage', { params: { ...params, request_id: requestId } });
  },

  async converterConvertCode(code: string, alcApiKey: string, alcConfigOverrides?: Record<string, any>) {
    return api.post('/api/v1/converter/convert/code',
      { code, ...(alcConfigOverrides ? { alc_config_overrides: alcConfigOverrides } : {}) },
      { headers: { 'X-API-Key': alcApiKey } }
    );
  },

  async converterGetConversions(params?: { api_key_ids?: string; limit?: number; offset?: number; sort?: string; order?: 'asc' | 'desc' }) {
    return api.get('/api/v1/converter/conversions', { params });
  },

  // Verbose alchemist log for one conversion. Needs the instance API key
  // header, so it cannot be a plain link.
  async converterGetLog(id: string): Promise<string> {
    const response = await api.get<string>(`/api/v1/converter/log/${id}`, { responseType: 'text' });
    return response.data;
  },

  async converterGetUsageDaily(params?: { from?: string; to?: string; api_key_ids?: string; status?: string }) {
    return api.get<{ days: ApiUsageDay[] }>('/api/v1/api/usage/daily', { params });
  },

  // CLI metering endpoints
  // Without operation_id: operations + aggregates. With operation_id: that operation plus its usage entries (404 if unknown).
  async cliGetUsage(params?: { limit?: number; offset?: number; operation?: string; status?: string; from?: string; to?: string; api_key_ids?: string; sort?: string; order?: 'asc' | 'desc' } & ({ operation_id?: undefined; billing_status?: never } | { operation_id: string; billing_status?: string })) {
    return api.get('/api/v1/cli/usage', { params });
  },

  async cliGetUsageDaily(params?: { from?: string; to?: string; api_key_ids?: string; operation?: string; status?: string }) {
    return api.get('/api/v1/cli/usage/daily', { params });
  },

  // WEB usage endpoints: usage entries of web analyzer and converter jobs.
  // Without job_id: jobs + aggregates. With job_id: that job plus its entries (404 if unknown).
  async webGetUsage(params?: { limit?: number; offset?: number; operation?: string; from?: string; to?: string; sort?: string; order?: 'asc' | 'desc' }) {
    return api.get<WebUsageListResponse>('/api/v1/web/usage', { params });
  },

  async webGetUsageJob(jobId: string, params?: { limit?: number; offset?: number; sort?: string; order?: 'asc' | 'desc' }) {
    return api.get<WebUsageDetailResponse>('/api/v1/web/usage', { params: { ...params, job_id: jobId } });
  },

  async webGetUsageDaily(params?: { from?: string; to?: string; operation?: string }) {
    return api.get<{ days: WebUsageDay[] }>('/api/v1/web/usage/daily', { params });
  },

  // Converter feature status
  async getFeatureStatus(): Promise<FeatureStatus> {
    const response = await api.get<FeatureStatus>("/api/v1/features/status");
    return response.data;
  },

  // Converter jobs
  async getConverterJobs(
    page: number = 1,
    status?: string,
    options?: { limit?: number; search?: string; sort?: string; order?: 'asc' | 'desc' }
  ): Promise<{ jobs: ConverterJob[]; total: number; page: number; total_pages: number; status_counts?: Record<string, number> }> {
    const params: any = { page, limit: options?.limit ?? 20 };
    if (status) params.status = status;
    if (options?.search) params.search = options.search;
    if (options?.sort) params.sort = options.sort;
    if (options?.order) params.order = options.order;

    const response = await api.get<{ jobs: ConverterJob[]; total: number; page: number; total_pages: number; status_counts?: Record<string, number> }>(
      "/api/v1/converter/jobs",
      { params }
    );
    return response.data;
  },

  async submitConverterJob(payload: ConverterJobSubmissionCode | ConverterJobSubmissionFile): Promise<{ job_id: string; status: string; position_in_queue?: number }> {
    if (payload.input_type === 'file') {
      const formData = new FormData();
      formData.append("file", payload.file);
      formData.append("input_type", "file");
      if (payload.project_name) {
        formData.append("project_name", payload.project_name);
      }
      if (payload.target_type) {
        formData.append("target_type", payload.target_type);
      }
      const response = await api.post<{ job_id: string; status: string; position_in_queue?: number }>(
        "/api/v1/converter/jobs",
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      return response.data;
    }

    const body: Record<string, any> = {
      input_type: 'code',
      code: payload.code,
    };
    if (payload.project_name) body.project_name = payload.project_name;
    if (payload.target_type) body.target_type = payload.target_type;

    const response = await api.post<{ job_id: string; status: string; position_in_queue?: number }>(
      "/api/v1/converter/jobs",
      body
    );
    return response.data;
  },

  async getConverterJob(jobId: string): Promise<ConverterJob> {
    const response = await api.get<ConverterJob>(`/api/v1/converter/jobs/${jobId}`);
    return response.data;
  },

  async deleteConverterJob(jobId: string): Promise<void> {
    await api.delete(`/api/v1/converter/jobs/${jobId}`);
  },

  async getConverterJobLogs(jobId: string, afterId?: number): Promise<JobLog[]> {
    const params: any = { limit: MAX_PAGE_LIMIT };
    if (afterId) params.after_id = afterId;

    const response = await api.get<JobLog[]>(`/api/v1/converter/jobs/${jobId}/logs`, { params });
    return response.data;
  },

  // Follows the after_id cursor until the last batch, so long logs are not cut at the page cap.
  async getAllConverterJobLogs(jobId: string): Promise<JobLog[]> {
    const all: JobLog[] = [];
    let afterId: number | undefined;
    for (;;) {
      const batch = await this.getConverterJobLogs(jobId, afterId);
      all.push(...batch);
      if (batch.length < MAX_PAGE_LIMIT) break;
      afterId = batch[batch.length - 1].id;
    }
    return all;
  },

  async getConverterJobResult(jobId: string): Promise<ConverterJobResult> {
    const response = await api.get<ConverterJobResult>(`/api/v1/converter/jobs/${jobId}/result`);
    return response.data;
  },
};
